import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatIDR, formatDate, formatDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Plane, Package, TrendingUp, Settings, ShieldCheck, Save, LogOut, ShoppingBag, Wallet, Search, KeyRound, Ban, CheckCircle2, X, Eye, AlertTriangle, ShieldAlert, Undo2, Banknote, ExternalLink, Mail, Phone, BookOpen, Activity, Sparkles, DollarSign, BadgeCheck, Trash2, Star, MessageSquare } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const ORDER_STATUS_LABELS = {
  pending_payment: "Menunggu Pembayaran",
  paid: "Dibayar (Escrow)",
  purchased: "Sudah Dibeli",
  shipping: "Sedang Dikirim",
  delivered: "Sampai",
  released: "Dana Dilepas",
  cancelled: "Dibatalkan",
  refunded: "Direfund",
  disputed: "Sengketa",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [buyerFee, setBuyerFee] = useState("");
  const [jastiperFee, setJastiperFee] = useState("");
  const [savingFee, setSavingFee] = useState(false);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [savingFlag, setSavingFlag] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/settings").then((r) => {
        setBuyerFee(String(r.data.buyer_platform_fee_percent ?? r.data.platform_fee_percent ?? 2));
        setJastiperFee(String(r.data.jastiper_platform_fee_percent ?? r.data.platform_fee_percent ?? 2));
        setLeaderboardEnabled(r.data.leaderboard_enabled !== false);
      }),
    ]).catch((err) => {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/admin/login");
      }
    }).finally(() => setLoading(false));
  }, []);

  const toggleLeaderboard = async (next) => {
    setSavingFlag(true);
    try {
      await api.put("/admin/settings", { leaderboard_enabled: next });
      setLeaderboardEnabled(next);
      toast.success(next ? "Leaderboard diaktifkan" : "Leaderboard disembunyikan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal update");
    } finally {
      setSavingFlag(false);
    }
  };

  const saveFee = async () => {
    const b = parseFloat(buyerFee);
    const j = parseFloat(jastiperFee);
    if (isNaN(b) || b < 0 || b > 50 || isNaN(j) || j < 0 || j > 50) {
      toast.error("Setiap fee harus angka 0–50%");
      return;
    }
    setSavingFee(true);
    try {
      await api.put("/admin/settings", { buyer_platform_fee_percent: b, jastiper_platform_fee_percent: j });
      toast.success(`Fee tersimpan — Pembeli ${b}%, Jastiper ${j}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan fee");
    } finally {
      setSavingFee(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  if (loading) return <div className="text-center py-20 text-zinc-500">Loading admin dashboard...</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4 border-b border-zinc-200 pb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center">
            <ShieldCheck size={22} strokeWidth={1.5} />
          </div>
          <div>
            <div className="label-caps text-zinc-500 mb-1">Superadmin Console</div>
            <h1 className="font-display text-3xl tracking-tight" data-testid="admin-heading">Platform Control</h1>
            <div className="text-xs text-zinc-500 font-mono mt-1">Signed in as {user?.email}</div>
          </div>
        </div>
        <button onClick={doLogout} className="btn-secondary-flat text-sm h-fit" data-testid="admin-logout-btn">
          <LogOut size={14} strokeWidth={1.5} /> Sign out
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border-t border-l border-zinc-200">
        <StatCard icon={Users} label="Total Users" value={stats?.users_total || 0} testid="admin-stat-users" />
        <StatCard icon={ShoppingBag} label="Buyers" value={stats?.buyers || 0} testid="admin-stat-buyers" />
        <StatCard icon={Plane} label="Jastipers" value={stats?.jastipers || 0} testid="admin-stat-jastipers" />
        <StatCard icon={Package} label="Active Orders" value={stats?.orders_active || 0} testid="admin-stat-active-orders" />
        <StatCard icon={Wallet} label="Released" value={stats?.orders_released || 0} testid="admin-stat-released" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-t border-l border-zinc-200">
        <StatCard icon={TrendingUp} label="Gross Merchandise Value" value={formatIDR(stats?.gmv || 0)} testid="admin-stat-gmv" big />
        <StatCard icon={Wallet} label="Platform Revenue" value={formatIDR(stats?.platform_revenue || 0)} testid="admin-stat-revenue" big accent />
      </div>

      {/* Platform Fee Settings — Two-sided (Buyer + Jastiper) */}
      <div className="border border-zinc-200 bg-white" data-testid="settings-section">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
          <Settings size={16} strokeWidth={1.5} />
          <div className="label-caps">Platform Fee Settings (Two-Sided)</div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-600">
            <strong>Buyer Fee</strong> ditambahkan di atas total bayar pembeli. <strong>Jastiper Fee</strong> dipotong dari penghasilan jastiper. Keduanya dapat diatur independen.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label-caps text-zinc-500 mb-2 block">Buyer Platform Fee</label>
              <p className="text-xs text-zinc-500 mb-2">Ditambahkan ke total bayar pembeli (% dari subtotal)</p>
              <div className="relative">
                <input
                  type="number" step="0.1" min="0" max="50"
                  className="input-flat pr-12 font-mono text-xl"
                  value={buyerFee}
                  onChange={(e) => setBuyerFee(e.target.value)}
                  data-testid="buyer-fee-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-zinc-400">%</span>
              </div>
            </div>
            <div>
              <label className="label-caps text-zinc-500 mb-2 block">Jastiper Platform Fee</label>
              <p className="text-xs text-zinc-500 mb-2">Dipotong dari penghasilan jastiper (% dari subtotal)</p>
              <div className="relative">
                <input
                  type="number" step="0.1" min="0" max="50"
                  className="input-flat pr-12 font-mono text-xl"
                  value={jastiperFee}
                  onChange={(e) => setJastiperFee(e.target.value)}
                  data-testid="jastiper-fee-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-zinc-400">%</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
            <div className="text-xs font-mono text-zinc-500">
              Total platform revenue = Buyer Fee + Jastiper Fee per order
            </div>
            <button onClick={saveFee} disabled={savingFee} className="btn-primary-flat" data-testid="save-fee-btn">
              <Save size={14} strokeWidth={1.5} />
              {savingFee ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </div>
      </div>

      {/* Iter 27 — Feature flags */}
      <div className="border border-zinc-200 bg-white p-6 space-y-4" data-testid="feature-flags-section">
        <div>
          <div className="label-caps">Feature Flags</div>
          <h2 className="font-display text-xl tracking-tight mt-1">Visibility Controls</h2>
        </div>
        <div className="flex items-center justify-between gap-4 p-3 border border-zinc-200">
          <div className="min-w-0">
            <div className="font-medium text-sm">Referral Leaderboard (Public)</div>
            <p className="text-xs text-zinc-500 mt-0.5">
              When enabled, anyone can view <code className="font-mono text-[11px]">/leaderboard</code> showing top referrers (anonymized). Disable to hide the page and remove its public CTA.
            </p>
          </div>
          <button
            type="button"
            onClick={() => toggleLeaderboard(!leaderboardEnabled)}
            disabled={savingFlag}
            className="relative inline-flex h-7 w-12 flex-shrink-0 items-center transition-colors"
            style={{backgroundColor: leaderboardEnabled ? "#059669" : "#D1D5DB"}}
            data-testid="leaderboard-toggle"
            aria-pressed={leaderboardEnabled}
          >
            <span
              className="inline-block h-5 w-5 transform bg-white transition-transform"
              style={{transform: leaderboardEnabled ? "translateX(24px)" : "translateX(4px)"}}
            />
          </button>
        </div>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start h-auto p-0 gap-0 overflow-x-auto">
          <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium" data-testid="tab-admin-users">
            User Management
          </TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium" data-testid="tab-admin-transactions">
            Transaksi
          </TabsTrigger>
          <TabsTrigger value="disputes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium" data-testid="tab-admin-disputes">
            <span className="inline-flex items-center gap-2">
              <AlertTriangle size={14} strokeWidth={1.5} /> Sengketa
            </span>
          </TabsTrigger>
          <TabsTrigger value="finance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-6 py-3 font-medium" data-testid="tab-admin-finance">
            <span className="inline-flex items-center gap-2">
              <Banknote size={14} strokeWidth={1.5} /> Keuangan & Midtrans
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6" data-testid="admin-users-tab">
          <UserManagement />
        </TabsContent>

        <TabsContent value="transactions" className="mt-6" data-testid="admin-transactions-tab">
          <TransactionManagement />
        </TabsContent>

        <TabsContent value="disputes" className="mt-6" data-testid="admin-disputes-tab">
          <DisputeManagement />
        </TabsContent>

        <TabsContent value="finance" className="mt-6" data-testid="admin-finance-tab">
          <FinanceOverview />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, big, accent }) {
  return (
    <div
      className="border-r border-b p-5"
      style={{
        borderColor: "var(--app-border)",
        backgroundColor: accent ? "var(--app-brand)" : "var(--app-surface)",
        color: accent ? "#FFFFFF" : "var(--app-fg)",
      }}
      data-testid={testid}
    >
      <Icon size={18} strokeWidth={1.5} className="mb-3" style={{ color: accent ? "rgba(255,255,255,0.85)" : "var(--app-brand)" }} />
      <div className="label-caps mb-2" style={{ color: accent ? "rgba(255,255,255,0.75)" : "var(--app-muted)" }}>{label}</div>
      <div className={`font-display ${big ? "text-3xl" : "text-2xl"} tracking-tight`}>{value}</div>
    </div>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [resetUser, setResetUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [generatedPw, setGeneratedPw] = useState(null);
  const [reviewsUser, setReviewsUser] = useState(null);  // user whose reviews are open
  const [reviewsData, setReviewsData] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // Iter 19: bulk-delete state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);

  const allVisibleIds = users.map((u) => u.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleIds));
    }
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runBulkDelete = async () => {
    if (bulkConfirmText !== "HAPUS") {
      toast.error("Ketik 'HAPUS' (huruf besar) untuk konfirmasi");
      return;
    }
    setBulkRunning(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await api.post("/admin/users/bulk-delete", { user_ids: ids });
      const { deleted_count, skipped_count, skipped } = r.data;
      if (skipped_count > 0) {
        toast.warning(`${deleted_count} dihapus, ${skipped_count} dilewati (lihat console).`);
        console.warn("[BulkDelete] Skipped:", skipped);
      } else {
        toast.success(`${deleted_count} user berhasil dihapus`);
      }
      setSelectedIds(new Set());
      setBulkDialogOpen(false);
      setBulkConfirmText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete gagal");
    } finally {
      setBulkRunning(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setSelectedIds(new Set());  // clear selection on reload
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    try {
      const r = await api.get(`/admin/users?${q.toString()}`);
      setUsers(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleSuspend = async (u) => {
    if (!window.confirm(`${u.suspended ? "Unsuspend" : "Suspend"} ${u.name}?`)) return;
    try {
      const r = await api.post(`/admin/users/${u.id}/suspend`);
      toast.success(r.data.suspended ? "User suspended" : "User reactivated");
      load();
    } catch {
      toast.error("Failed to toggle suspension");
    }
  };

  const toggleVerifiedBadge = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.id}/toggle-verified-badge`);
      toast.success(r.data.verified_badge ? `${u.name} sekarang Verified ✓` : `Verified dicabut dari ${u.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal toggle verified");
    }
  };

  const submitReset = async () => {
    if (!resetUser) return;
    try {
      const r = await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPw });
      setGeneratedPw(r.data.new_password);
      toast.success("Password reset successfully");
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  const openReviews = async (u) => {
    setReviewsUser(u);
    setReviewsData(null);
    setReviewsLoading(true);
    try {
      const r = await api.get(`/admin/users/${u.id}/reviews`);
      setReviewsData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat review");
      setReviewsUser(null);
    } finally {
      setReviewsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteUser) return;
    if (deleteConfirmText !== "HAPUS") {
      toast.error("Ketik 'HAPUS' (huruf besar) untuk konfirmasi");
      return;
    }
    try {
      const r = await api.delete(`/admin/users/${deleteUser.id}`);
      toast.success(`User ${deleteUser.name} dihapus`);
      setDeleteUser(null);
      setDeleteConfirmText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus user");
    }
  };

  return (
    <div className="space-y-4" data-testid="user-management-section">
      <div className="border p-4 flex flex-col sm:flex-row gap-3" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}}>
        <div className="relative flex-1">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{color: "var(--app-muted)"}} />
          <input
            className="input-flat pl-9"
            placeholder="Cari nama, email, atau nomor HP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="user-search-input"
          />
        </div>
        <button onClick={load} className="btn-primary-flat" data-testid="apply-user-filter-btn">Cari</button>
      </div>

      {/* Bulk action bar — shown only when something is selected */}
      {selectedIds.size > 0 && (
        <div
          className="border p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ borderColor: "var(--app-brand)", backgroundColor: "var(--app-brand-soft)" }}
          data-testid="bulk-action-bar"
        >
          <div className="text-sm">
            <span className="font-semibold" style={{ color: "var(--app-brand)" }} data-testid="bulk-selected-count">
              {selectedIds.size}
            </span>{" "}
            user terpilih
            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-3 text-xs underline"
              style={{ color: "var(--app-muted)" }}
              data-testid="bulk-clear-btn"
            >
              Bersihkan
            </button>
          </div>
          <button
            onClick={() => { setBulkDialogOpen(true); setBulkConfirmText(""); }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 inline-flex items-center gap-2"
            data-testid="bulk-delete-open-btn"
          >
            <Trash2 size={14} strokeWidth={2} />
            Hapus {selectedIds.size} user terpilih
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12" style={{color: "var(--app-muted)"}}>Memuat user...</div>
      ) : (
        <div className="border overflow-x-auto" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface)"}}>
          <table className="text-sm w-full min-w-[1100px]">
            <thead className="border-b" style={{borderColor: "var(--app-border)", backgroundColor: "var(--app-surface-alt)"}}>
              <tr className="text-left">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                    className="cursor-pointer w-4 h-4 align-middle"
                    title={allSelected ? "Batal pilih semua" : "Pilih semua"}
                    data-testid="bulk-select-all-checkbox"
                  />
                </th>
                <th className="px-3 py-3 label-caps">Nama</th>
                <th className="px-3 py-3 label-caps">Email</th>
                <th className="px-3 py-3 label-caps">No. Telp</th>
                <th className="px-3 py-3 label-caps">Status</th>
                <th className="px-3 py-3 label-caps text-right">Transaksi</th>
                <th className="px-3 py-3 label-caps text-right">Insights</th>
                <th className="px-3 py-3 label-caps">Bergabung</th>
                <th
                  className="px-3 py-3 label-caps text-right sticky right-0 border-l shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]"
                  style={{
                    backgroundColor: "var(--app-surface-alt)",
                    borderColor: "var(--app-border)",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const txTotal = u.tx_count || 0;
                const txOk = u.tx_success_count || 0;
                const reviewsN = u.reviews_received_count || 0;
                const avg = u.reviews_avg_rating;
                return (
                  <tr key={u.id} className="border-b" style={{borderColor: "var(--app-border)", backgroundColor: selectedIds.has(u.id) ? "var(--app-brand-soft)" : "transparent"}} data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => toggleOne(u.id)}
                        className="cursor-pointer w-4 h-4 align-middle"
                        data-testid={`bulk-select-row-${u.id}`}
                      />
                    </td>
                    <td className="px-3 py-3 font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        {u.name}
                        {u.verified_badge && <VerifiedBadge size={13} testid={`user-verified-${u.id}`} />}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{u.email}</td>
                    <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{u.phone || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {u.suspended ? (
                        <span className="label-caps px-2 py-1 bg-red-50 text-red-700">Suspended</span>
                      ) : u.verified ? (
                        <span className="label-caps px-2 py-1 bg-green-50 text-green-700">Active</span>
                      ) : (
                        <span className="label-caps px-2 py-1 bg-amber-50 text-amber-700">Unverified</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap" data-testid={`user-tx-${u.id}`}>
                      <div className="font-mono text-sm font-semibold">{txTotal}</div>
                      <div className="text-[10px] font-mono" style={{color: "var(--app-muted)"}}>
                        <span className="text-green-600 dark:text-green-400">{txOk}</span> sukses
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap" data-testid={`user-insights-${u.id}`}>
                      {(u.total_spend || 0) > 0 && (
                        <div className="text-[11px]">
                          <span style={{color: "var(--app-muted)"}}>Belanja:</span>{" "}
                          <span className="font-mono font-semibold" style={{color: "var(--app-brand)"}}>{formatIDR(u.total_spend)}</span>
                        </div>
                      )}
                      {(u.total_earned || 0) > 0 && (
                        <div className="text-[11px]">
                          <span style={{color: "var(--app-muted)"}}>Cuan:</span>{" "}
                          <span className="font-mono font-semibold text-green-700 dark:text-green-400">{formatIDR(u.total_earned)}</span>
                        </div>
                      )}
                      {(u.pending_earnings || 0) > 0 && (
                        <div className="text-[10px] font-mono text-amber-600">+{formatIDR(u.pending_earnings)} pending</div>
                      )}
                      {(u.total_spend || 0) === 0 && (u.total_earned || 0) === 0 && (
                        <div className="text-[10px] font-mono" style={{color: "var(--app-muted)"}}>—</div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs" style={{color: "var(--app-muted)"}}>{formatDate(u.created_at)}</td>
                    <td
                      className="px-3 py-3 text-right sticky right-0 border-l shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]"
                      style={{
                        backgroundColor: "var(--app-surface)",
                        borderColor: "var(--app-border)",
                      }}
                    >
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => openReviews(u)}
                          className="h-8 px-2 border flex items-center gap-1 text-xs"
                          style={{borderColor: "var(--app-border)", color: reviewsN > 0 ? "var(--app-brand)" : "var(--app-muted)"}}
                          title={`Lihat ${reviewsN} review`}
                          data-testid={`reviews-btn-${u.id}`}
                        >
                          <Star size={12} strokeWidth={2} fill={reviewsN > 0 ? "currentColor" : "none"} />
                          <span className="font-mono">{reviewsN}</span>
                          {avg != null && <span className="font-mono">·{avg.toFixed(1)}</span>}
                        </button>
                        <button
                          onClick={() => toggleVerifiedBadge(u)}
                          className="w-8 h-8 border flex items-center justify-center"
                          style={{
                            borderColor: u.verified_badge ? "#1d9bf0" : "var(--app-border)",
                            color: u.verified_badge ? "#1d9bf0" : "var(--app-muted)",
                            backgroundColor: u.verified_badge ? "rgba(29,155,240,0.12)" : "transparent",
                          }}
                          title={u.verified_badge ? "Cabut Verified Badge" : "Beri Verified Badge"}
                          data-testid={`toggle-verified-btn-${u.id}`}
                        >
                          <BadgeCheck size={14} strokeWidth={2} />
                        </button>
                        <button
                          onClick={() => { setResetUser(u); setGeneratedPw(null); setNewPw(""); }}
                          className="w-8 h-8 border flex items-center justify-center hover:bg-[var(--app-brand)] hover:text-white"
                          style={{borderColor: "var(--app-border)"}}
                          title="Reset Password"
                          data-testid={`reset-pw-btn-${u.id}`}
                        >
                          <KeyRound size={13} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => toggleSuspend(u)}
                          className={`w-8 h-8 border flex items-center justify-center ${u.suspended ? "border-green-300 text-green-700 hover:bg-green-600 hover:text-white" : "hover:bg-amber-600 hover:text-white hover:border-amber-600"}`}
                          style={!u.suspended ? {borderColor: "var(--app-border)"} : {}}
                          title={u.suspended ? "Aktifkan" : "Suspend"}
                          data-testid={`suspend-btn-${u.id}`}
                        >
                          {u.suspended ? <CheckCircle2 size={13} strokeWidth={1.5} /> : <Ban size={13} strokeWidth={1.5} />}
                        </button>
                        <button
                          onClick={() => { setDeleteUser(u); setDeleteConfirmText(""); }}
                          className="w-8 h-8 border flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600"
                          style={{borderColor: "var(--app-border)"}}
                          title="Hapus user permanen"
                          data-testid={`delete-user-btn-${u.id}`}
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center" style={{color: "var(--app-muted)"}}>Tidak ada user ditemukan</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reviews dialog */}
      <Dialog open={!!reviewsUser} onOpenChange={(o) => { if (!o) { setReviewsUser(null); setReviewsData(null); } }}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="user-reviews-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
              <Star size={16} strokeWidth={2} /> Review yang diterima — {reviewsUser?.name}
            </DialogTitle>
            <DialogDescription>
              {reviewsData?.count ?? 0} review · rata-rata {reviewsUser?.reviews_avg_rating ? reviewsUser.reviews_avg_rating.toFixed(2) : "—"} ⭐
            </DialogDescription>
          </DialogHeader>
          {reviewsLoading ? (
            <div className="py-8 text-center" style={{color: "var(--app-muted)"}}>Memuat review...</div>
          ) : reviewsData?.reviews?.length ? (
            <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="user-reviews-list">
              {reviewsData.reviews.map((rv) => (
                <div key={`${rv.order_id}-${rv.from_user_id}`} className="border p-3" style={{borderColor: "var(--app-border)"}}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-medium">{rv.reviewer_name}</div>
                    <div className="flex items-center gap-0.5" data-testid="review-rating">
                      {[1,2,3,4,5].map((i) => (
                        <Star key={i} size={12} strokeWidth={2} fill={i <= (rv.rating || 0) ? "#FBBF24" : "none"} stroke={i <= (rv.rating || 0) ? "#FBBF24" : "currentColor"} style={{color: i <= (rv.rating || 0) ? "#FBBF24" : "var(--app-muted)"}} />
                      ))}
                      <span className="text-xs font-mono ml-1">{rv.rating}/5</span>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed" style={{color: "var(--app-fg)"}}>{rv.comment || <em style={{color:"var(--app-muted)"}}>(tanpa komentar)</em>}</p>
                  <div className="mt-1 text-[10px] font-mono" style={{color: "var(--app-muted)"}}>
                    {rv.created_at ? formatDate(rv.created_at) : ""} · order #{(rv.order_id || "").slice(0,8)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center" style={{color: "var(--app-muted)"}}>
              <MessageSquare size={28} strokeWidth={1.5} className="mx-auto mb-2 opacity-50" />
              Belum ada review untuk user ini.
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteUser} onOpenChange={(o) => { if (!o) { setDeleteUser(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="delete-user-dialog">
          {deleteUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2 text-red-700 dark:text-red-400">
                  <Trash2 size={16} strokeWidth={2} /> Hapus user permanen?
                </DialogTitle>
                <DialogDescription data-testid="delete-user-dialog-desc">
                  Anda akan menghapus <strong>{deleteUser.name}</strong> ({deleteUser.email}). Tindakan ini TIDAK dapat di-undo.
                  Katalog, trip, request, review, dan token notifikasi user ini juga akan dihapus.
                  Riwayat order tetap disimpan untuk audit (nama buyer/jastiper akan menjadi <em>"[Deleted user]"</em>).
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <label className="text-xs uppercase tracking-wide" style={{color:"var(--app-muted)"}}>
                  Ketik <strong className="font-mono">HAPUS</strong> untuk mengonfirmasi:
                </label>
                <input
                  className="input-flat"
                  autoFocus
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="HAPUS"
                  data-testid="delete-user-confirm-input"
                />
                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={() => { setDeleteUser(null); setDeleteConfirmText(""); }}
                    className="btn-secondary-flat text-sm"
                    data-testid="delete-user-cancel-btn"
                  >
                    Batal
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleteConfirmText !== "HAPUS"}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="delete-user-confirm-btn"
                  >
                    <Trash2 size={14} strokeWidth={2} className="inline mr-1" /> Hapus Permanen
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(o) => { if (!o && !bulkRunning) { setBulkDialogOpen(false); setBulkConfirmText(""); } }}>
        <DialogContent className="rounded-none max-w-md" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2 text-red-700 dark:text-red-400">
              <Trash2 size={16} strokeWidth={2} /> Hapus {selectedIds.size} user sekaligus?
            </DialogTitle>
            <DialogDescription data-testid="bulk-delete-dialog-desc">
              Tindakan ini TIDAK dapat di-undo. Untuk setiap user, katalog/trip/request/review/notifikasi
              akan dihapus. Order yang sudah selesai tetap disimpan untuk audit. User dengan order yang
              masih berjalan akan dilewati otomatis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-xs uppercase tracking-wide" style={{color:"var(--app-muted)"}}>
              Ketik <strong className="font-mono">HAPUS</strong> untuk mengonfirmasi:
            </label>
            <input
              className="input-flat"
              autoFocus
              value={bulkConfirmText}
              onChange={(e) => setBulkConfirmText(e.target.value)}
              placeholder="HAPUS"
              disabled={bulkRunning}
              data-testid="bulk-delete-confirm-input"
            />
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => { setBulkDialogOpen(false); setBulkConfirmText(""); }}
                disabled={bulkRunning}
                className="btn-secondary-flat text-sm"
                data-testid="bulk-delete-cancel-btn"
              >
                Batal
              </button>
              <button
                onClick={runBulkDelete}
                disabled={bulkConfirmText !== "HAPUS" || bulkRunning}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-delete-confirm-btn"
              >
                {bulkRunning ? "Menghapus..." : <><Trash2 size={14} strokeWidth={2} className="inline mr-1" /> Hapus {selectedIds.size} user</>}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(o) => { if (!o) { setResetUser(null); setGeneratedPw(null); } }}>
        <DialogContent className="rounded-none" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for <strong>{resetUser?.name}</strong> ({resetUser?.email}).
              {!generatedPw && " Enter a new password or leave empty to auto-generate."}
            </DialogDescription>
          </DialogHeader>
          {generatedPw ? (
            <div className="space-y-3">
              <div className="border border-amber-200 bg-amber-50 p-4 font-mono text-sm">
                <div className="text-xs label-caps text-amber-700 mb-2">New Password (share securely)</div>
                <div className="text-lg select-all" data-testid="generated-password">{generatedPw}</div>
              </div>
              <button onClick={() => { setResetUser(null); setGeneratedPw(null); }} className="btn-primary-flat w-full" data-testid="close-pw-dialog-btn">Done</button>
            </div>
          ) : (
            <div className="space-y-3">
              <input
                type="text"
                className="input-flat"
                placeholder="New password (min 6 chars) or leave empty"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="new-password-input"
              />
              <button onClick={submitReset} className="btn-primary-flat w-full" data-testid="confirm-reset-pw-btn">Reset Password</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransactionManagement() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailOrder, setDetailOrder] = useState(null);

  const load = async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (statusFilter) q.set("status", statusFilter);
    try {
      const r = await api.get(`/admin/orders?${q.toString()}`);
      setOrders(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (orderId) => {
    try {
      const r = await api.get(`/admin/orders/${orderId}`);
      setDetailOrder(r.data);
    } catch {
      toast.error("Failed to load order details");
    }
  };

  return (
    <div className="space-y-4" data-testid="transaction-management-section">
      <div className="border border-zinc-200 bg-white p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            className="input-flat pl-9"
            placeholder="Search by buyer or jastiper name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="tx-search-input"
          />
        </div>
        <select className="input-flat sm:w-56" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="tx-status-filter">
          <option value="">All Statuses</option>
          {Object.entries(ORDER_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button onClick={load} className="btn-primary-flat" data-testid="apply-tx-filter-btn">Apply</button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading transactions...</div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps text-zinc-500">Order ID</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Buyer</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Jastiper</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Item Detail</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Total</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Status</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Tanggal</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Detail</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-tx-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">{o.buyer_name}</td>
                  <td className="px-4 py-3">{o.jastiper_name}</td>
                  <td className="px-4 py-3 max-w-xs truncate">
                    {o.items?.[0]?.name}
                    {(o.items?.length || 0) > 1 && <span className="text-zinc-500"> +{o.items.length - 1}</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-right">{formatIDR(o.gross_amount)}</td>
                  <td className="px-4 py-3">
                    <span className="label-caps px-2 py-1 bg-zinc-100">{ORDER_STATUS_LABELS[o.escrow_status] || o.escrow_status}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">{formatDate(o.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openDetail(o.id)}
                      className="w-8 h-8 border border-zinc-200 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] flex items-center justify-center ml-auto"
                      title="View detail"
                      data-testid={`view-tx-btn-${o.id}`}
                    >
                      <Eye size={13} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400">No transactions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detailOrder} onOpenChange={(o) => !o && setDetailOrder(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tx-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Transaction Details</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Order #{detailOrder?.id?.slice(0, 12).toUpperCase()} · {formatDateTime(detailOrder?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {detailOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-zinc-200 p-4">
                  <div className="label-caps text-zinc-500 mb-2">Pembeli</div>
                  <div className="font-medium">{detailOrder.buyer_name}</div>
                  <div className="text-xs font-mono text-zinc-500">{detailOrder.buyer?.email}</div>
                  <div className="text-xs font-mono text-zinc-500">{detailOrder.buyer?.phone}</div>
                </div>
                <div className="border border-zinc-200 p-4">
                  <div className="label-caps text-zinc-500 mb-2">Jastiper</div>
                  <div className="font-medium">{detailOrder.jastiper_name}</div>
                  <div className="text-xs font-mono text-zinc-500">{detailOrder.jastiper?.email}</div>
                  <div className="text-xs font-mono text-zinc-500">{detailOrder.jastiper?.phone}</div>
                </div>
              </div>

              <div className="border border-zinc-200 p-4">
                <div className="label-caps text-zinc-500 mb-2">Item / Request</div>
                <div className="space-y-2">
                  {detailOrder.items?.map((it, idx) => (
                    <div key={idx} className="flex gap-3 items-center border-b border-zinc-100 last:border-b-0 pb-2 last:pb-0">
                      {it.photo_path && <img src={fileUrl(it.photo_path)} className="w-12 h-12 object-cover border border-zinc-200" alt={it.name} />}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{it.name}</div>
                        <div className="text-xs text-zinc-500">Qty {it.qty || 1} · {formatIDR((it.price || 0) * (it.qty || 1))}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-zinc-200 p-4">
                <div className="label-caps text-zinc-500 mb-3">Pembayaran Breakdown</div>
                <div className="font-mono text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-zinc-500">Item Total</span><span>{formatIDR(detailOrder.item_total)}</span></div>
                  {(detailOrder.service_fee || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-zinc-500">Jastiper Fee (legacy)</span><span>{formatIDR(detailOrder.service_fee)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-zinc-500">Shipping</span><span>{formatIDR(detailOrder.shipping_fee || 0)}</span></div>
                  <div className="flex justify-between border-t border-zinc-200 pt-1 mt-1 font-semibold"><span>Gross (Buyer Paid)</span><span>{formatIDR(detailOrder.gross_amount)}</span></div>
                  <div className="flex justify-between text-zinc-500 text-xs border-t border-zinc-200 pt-1 mt-1"><span>Platform Fee</span><span>{formatIDR(detailOrder.platform_fee)}</span></div>
                  <div className="flex justify-between text-[#002FA7] font-semibold"><span>Net to Jastiper</span><span>{formatIDR(detailOrder.net_to_jastiper)}</span></div>
                </div>
              </div>

              <div className="border border-zinc-200 p-4">
                <div className="label-caps text-zinc-500 mb-2">Escrow Status</div>
                <span className="label-caps px-2 py-1 bg-zinc-100">{ORDER_STATUS_LABELS[detailOrder.escrow_status] || detailOrder.escrow_status}</span>
                {detailOrder.mock_transaction_id && <div className="text-xs font-mono text-zinc-500 mt-2">Tx: {detailOrder.mock_transaction_id}</div>}
              </div>

              {(detailOrder.proof_path || detailOrder.receipt_path) && (
                <div className="border border-zinc-200 p-4">
                  <div className="label-caps text-zinc-500 mb-2">Bukti Pembelian</div>
                  <img src={fileUrl(detailOrder.proof_path || detailOrder.receipt_path)} alt="Bukti Pembelian" className="max-h-64 border border-zinc-200" data-testid="tx-detail-proof-img" />
                  {detailOrder.status_note && (
                    <div className="mt-2 text-sm"><span className="text-zinc-500">Status:</span> <span className="font-medium">{detailOrder.status_note}</span></div>
                  )}
                  {(detailOrder.keterangan || detailOrder.tracking_number) && (
                    <div className="text-sm"><span className="text-zinc-500">Keterangan:</span> <span className="font-mono">{detailOrder.keterangan || detailOrder.tracking_number}</span></div>
                  )}
                </div>
              )}

              {detailOrder.timeline && detailOrder.timeline.length > 0 && (
                <div className="border border-zinc-200 p-4">
                  <div className="label-caps text-zinc-500 mb-2">Timeline</div>
                  <div className="space-y-2">
                    {detailOrder.timeline.map((t, idx) => (
                      <div key={idx} className="text-xs font-mono flex gap-3">
                        <span className="text-zinc-400 w-32">{formatDateTime(t.at)}</span>
                        <span className="font-semibold uppercase w-24">{t.status}</span>
                        <span className="text-zinc-600 flex-1">{t.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DisputeManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evidence, setEvidence] = useState(null);
  const [resolutionNote, setResolutionNote] = useState("");
  const [resolving, setResolving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/orders?status=disputed");
      setOrders(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEvidence = async (orderId) => {
    try {
      const r = await api.get(`/admin/orders/${orderId}/evidence`);
      setEvidence(r.data);
      setResolutionNote("");
    } catch {
      toast.error("Gagal memuat bukti");
    }
  };

  const resolve = async (action) => {
    if (!evidence?.order) return;
    const label = action === "force-release" ? "lepas dana ke Jastiper" : "refund penuh ke Buyer";
    if (!window.confirm(`Yakin ${label}? Aksi ini tidak bisa dibatalkan.`)) return;
    setResolving(true);
    try {
      const endpoint = action === "force-release"
        ? `/admin/orders/${evidence.order.id}/force-release`
        : `/admin/orders/${evidence.order.id}/full-refund`;
      await api.post(endpoint, { resolution_note: resolutionNote || `Admin: ${label}` });
      toast.success(`Sengketa diselesaikan (${label}).`);
      setEvidence(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyelesaikan");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dispute-management-section">
      {loading ? (
        <div className="text-center py-12 text-zinc-500">Memuat sengketa...</div>
      ) : orders.length === 0 ? (
        <div className="border border-zinc-200 bg-white py-16 text-center" data-testid="no-disputes">
          <ShieldCheck size={32} strokeWidth={1.5} className="mx-auto text-green-500 mb-3" />
          <p className="text-zinc-600">Tidak ada sengketa aktif. 🎉</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps text-zinc-500">Order ID</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Pembeli</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Jastiper</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Dilaporkan</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Alasan</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Total</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Detail</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-dispute-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">{o.buyer_name}</td>
                  <td className="px-4 py-3">{o.jastiper_name}</td>
                  <td className="px-4 py-3 text-xs">{o.dispute_reporter_name || "—"}</td>
                  <td className="px-4 py-3 max-w-xs truncate text-xs italic">"{o.dispute_reason || ""}"</td>
                  <td className="px-4 py-3 font-mono text-right">{formatIDR(o.gross_amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEvidence(o.id)}
                      className="w-8 h-8 border border-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center justify-center ml-auto"
                      title="Tinjau bukti & selesaikan"
                      data-testid={`view-evidence-btn-${o.id}`}
                    >
                      <Eye size={13} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!evidence} onOpenChange={(o) => !o && setEvidence(null)}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="evidence-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <AlertTriangle size={18} strokeWidth={1.5} className="text-red-600" />
              Resolusi Sengketa
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Order #{evidence?.order?.id?.slice(0, 12).toUpperCase()} ·{" "}
              {evidence?.message_count ?? 0} pesan chat · {evidence?.photos?.length ?? 0} foto bukti
            </DialogDescription>
          </DialogHeader>
          {evidence && (
            <div className="space-y-4">
              {/* Dispute statement */}
              <div className="border-2 border-red-200 bg-red-50 p-4">
                <div className="label-caps text-red-800 mb-1">Laporan</div>
                <div className="text-sm">
                  Dilaporkan oleh <strong>{evidence.order.dispute_reporter_name}</strong>
                </div>
                <p className="text-sm italic mt-1">"{evidence.order.dispute_reason}"</p>
              </div>

              {/* Parties */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-zinc-200 p-3 text-xs">
                  <div className="label-caps mb-1">Pembeli</div>
                  <div className="font-medium text-sm">{evidence.buyer?.name}</div>
                  <div className="font-mono text-zinc-500">{evidence.buyer?.email}</div>
                </div>
                <div className="border border-zinc-200 p-3 text-xs">
                  <div className="label-caps mb-1">Jastiper</div>
                  <div className="font-medium text-sm">{evidence.jastiper?.name}</div>
                  <div className="font-mono text-zinc-500">{evidence.jastiper?.email}</div>
                </div>
              </div>

              {/* Photos */}
              {evidence.photos?.length > 0 && (
                <div className="border border-zinc-200 p-4">
                  <div className="label-caps mb-2">Foto Bukti</div>
                  <div className="grid grid-cols-3 gap-2">
                    {evidence.photos.map((p, i) => (
                      <div key={i} className="space-y-1" data-testid={`evidence-photo-${i}`}>
                        <img src={fileUrl(p.path)} alt={p.label} className="w-full aspect-square object-cover border border-zinc-200" />
                        <div className="text-xs text-center text-zinc-500 truncate">{p.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat log */}
              <div className="border border-zinc-200 p-4">
                <div className="label-caps mb-2">Log Chat ({evidence.message_count})</div>
                <div className="max-h-64 overflow-y-auto space-y-2" data-testid="evidence-chat-log">
                  {(evidence.messages || []).length === 0 ? (
                    <div className="text-xs text-zinc-400 text-center py-4">Tidak ada chat</div>
                  ) : (
                    evidence.messages.map((m) => (
                      <div key={m.id} className="text-xs border-l-2 border-zinc-200 pl-2">
                        <div className="font-medium">{m.sender_name}</div>
                        <div className="text-zinc-600">{m.text}</div>
                        <div className="font-mono text-zinc-400 text-[10px]">{formatDateTime(m.created_at)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Resolution */}
              <div className="border-2 border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="label-caps text-amber-800">Selesaikan Sengketa</div>
                <textarea
                  className="input-flat text-sm min-h-[70px]"
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  placeholder="Catatan resolusi (opsional)"
                  data-testid="resolution-note-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => resolve("force-release")}
                    disabled={resolving}
                    className="btn-primary-flat text-sm"
                    data-testid="force-release-btn"
                  >
                    <ShieldAlert size={14} strokeWidth={1.5} /> Lepas Dana ke Jastiper
                  </button>
                  <button
                    onClick={() => resolve("full-refund")}
                    disabled={resolving}
                    className="text-sm py-2 border-2 inline-flex items-center justify-center gap-2 rounded-md bg-white hover:bg-red-50"
                    style={{borderColor: "#FECACA", color: "#991B1B"}}
                    data-testid="full-refund-btn"
                  >
                    <Undo2 size={14} strokeWidth={1.5} /> Refund ke Buyer
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Finance & Midtrans Overview ────────────────────────────────────────────
const STATUS_BADGE = {
  settled: { bg: "#DCFCE7", text: "#166534", label: "Settled" },
  pending: { bg: "#FEF3C7", text: "#92400E", label: "Pending" },
  on_hold: { bg: "#FECACA", text: "#7F1D1D", label: "On Hold" },
  refunded: { bg: "#E0E7FF", text: "#3730A3", label: "Refunded" },
  cancelled: { bg: "#F4F4F5", text: "#71717A", label: "Cancelled" },
};

function FinanceOverview() {
  const [overview, setOverview] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [midtrans, setMidtrans] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/finance/midtrans-info").then((r) => setMidtrans(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/finance/overview?days=${days}`),
      api.get(`/admin/finance/settlements${filterStatus ? `?status=${filterStatus}` : ""}`),
    ])
      .then(([o, s]) => {
        setOverview(o.data);
        setSettlements(s.data.settlements);
      })
      .finally(() => setLoading(false));
  }, [days, filterStatus]);

  if (loading || !overview) {
    return <div className="text-center py-12 text-zinc-500">Memuat data keuangan...</div>;
  }

  const lt = overview.lifetime;
  const m = overview.midtrans;
  const chartData = overview.daily.map((d) => ({
    date: d.date.slice(5),  // MM-DD
    Omset: d.omset,
    "Platform Fee": d.platform_fee,
  }));

  return (
    <div className="space-y-6" data-testid="finance-overview">
      {/* Mock disclaimer */}
      <div className="border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-xs" data-testid="finance-mock-banner">
        <Sparkles size={14} strokeWidth={1.5} className="text-amber-700" />
        <span className="text-amber-800">
          <strong>Mode Demo</strong> — Saldo & settlement Midtrans saat ini disimulasikan dari order Escrow internal. Ganti ke API live ketika Midtrans diaktifkan.
        </span>
      </div>

      {/* Midtrans Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          icon={Wallet}
          label="Saldo Tersedia (Midtrans)"
          value={formatIDR(m.available_balance)}
          subtext={`Per ${new Date(m.as_of).toLocaleString("id-ID")}`}
          highlight
          testid="metric-midtrans-available"
        />
        <MetricCard
          icon={ShieldCheck}
          label="Dana di Escrow (Held)"
          value={formatIDR(m.held_balance)}
          subtext="Belum dilepas ke Jastiper"
          testid="metric-midtrans-held"
        />
        <MetricCard
          icon={DollarSign}
          label="Total Profit Owner"
          value={formatIDR(lt.platform_revenue)}
          subtext={`Dari ${lt.orders} order lifetime`}
          highlight
          testid="metric-platform-revenue"
        />
      </div>

      {/* Lifetime totals */}
      <div className="border border-zinc-200 bg-white p-6" data-testid="lifetime-totals">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label-caps text-zinc-500">Total Lifetime</div>
            <h2 className="font-display text-2xl tracking-tight">Performa Platform</h2>
          </div>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="input-flat w-40 text-sm"
            data-testid="chart-period-select"
          >
            <option value={7}>7 hari terakhir</option>
            <option value={14}>14 hari terakhir</option>
            <option value={30}>30 hari terakhir</option>
            <option value={90}>90 hari terakhir</option>
          </select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <CompactStat label="GMV" value={formatIDR(lt.gmv)} />
          <CompactStat label="Platform Fee" value={formatIDR(lt.platform_revenue)} accent />
          <CompactStat label="Jastiper Payout" value={formatIDR(lt.jastiper_payout)} />
          <CompactStat label="Orders" value={String(lt.orders)} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-zinc-100 p-4" data-testid="omset-chart">
            <div className="label-caps text-zinc-500 mb-3 flex items-center gap-2">
              <Activity size={12} strokeWidth={1.5} /> Total Omset (GMV) per Hari
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Line type="monotone" dataKey="Omset" stroke="#002FA7" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-zinc-100 p-4" data-testid="platform-fee-chart">
            <div className="label-caps text-zinc-500 mb-3 flex items-center gap-2">
              <TrendingUp size={12} strokeWidth={1.5} /> Profit Owner (Platform Fee) per Hari
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Bar dataKey="Platform Fee" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Settlements Table */}
      <div className="border border-zinc-200 bg-white" data-testid="settlements-section">
        <div className="border-b border-zinc-200 p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-caps text-zinc-500">Midtrans Settlements</div>
            <div className="font-display text-lg tracking-tight">Status Penyelesaian Transaksi</div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-flat w-44 text-sm"
            data-testid="settlement-filter-status"
          >
            <option value="">Semua Status</option>
            <option value="settled">Settled</option>
            <option value="pending">Pending</option>
            <option value="on_hold">On Hold</option>
            <option value="refunded">Refunded</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-caps text-zinc-500">Settlement ID</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Pembeli</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Jastiper</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Gross</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Platform Fee</th>
                <th className="px-4 py-3 label-caps text-zinc-500 text-right">Net Payout</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Status</th>
                <th className="px-4 py-3 label-caps text-zinc-500">Settled</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => {
                const badge = STATUS_BADGE[s.settlement_status] || STATUS_BADGE.pending;
                return (
                  <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`settlement-row-${s.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{s.id}</td>
                    <td className="px-4 py-3">{s.buyer_name || "—"}</td>
                    <td className="px-4 py-3">{s.jastiper_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-right">{formatIDR(s.gross_amount)}</td>
                    <td className="px-4 py-3 font-mono text-right text-green-700">{formatIDR(s.platform_fee)}</td>
                    <td className="px-4 py-3 font-mono text-right">{formatIDR(s.net_to_jastiper)}</td>
                    <td className="px-4 py-3">
                      <span
                        className="label-caps px-2 py-1 font-mono text-[10px]"
                        style={{ backgroundColor: badge.bg, color: badge.text }}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      {s.settled_at ? formatDateTime(s.settled_at) : "—"}
                    </td>
                  </tr>
                );
              })}
              {settlements.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-400">Tidak ada settlement</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Midtrans Support & Dashboard Links */}
      {midtrans && (
        <div className="border border-zinc-200 bg-white p-6" data-testid="midtrans-support">
          <div className="label-caps text-zinc-500 mb-3">Akses Midtrans</div>
          <h3 className="font-display text-xl tracking-tight mb-4">Dashboard & Support Resmi</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <ExternalLinkCard
              href={midtrans.dashboard_url}
              icon={ExternalLink}
              label="Buka Dashboard Midtrans"
              sub="dashboard.midtrans.com"
              accent
              testid="link-midtrans-dashboard"
            />
            <ExternalLinkCard
              href={midtrans.docs_url}
              icon={BookOpen}
              label="Dokumentasi API"
              sub="docs.midtrans.com"
              testid="link-midtrans-docs"
            />
            <ExternalLinkCard
              href={midtrans.status_page_url}
              icon={Activity}
              label="Status Layanan"
              sub="status.midtrans.com"
              testid="link-midtrans-status"
            />
            <ExternalLinkCard
              href={midtrans.support_url}
              icon={Mail}
              label="Hubungi Support"
              sub={midtrans.support_email}
              testid="link-midtrans-support"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono text-zinc-600">
            <div className="flex items-center gap-2">
              <Mail size={12} strokeWidth={1.5} />
              <a href={`mailto:${midtrans.support_email}`} className="hover:underline" data-testid="support-email-link">
                {midtrans.support_email}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Phone size={12} strokeWidth={1.5} />
              <a href={`tel:${midtrans.support_phone.replace(/\s/g, "")}`} className="hover:underline" data-testid="support-phone-link">
                {midtrans.support_phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, subtext, highlight, testid }) {
  return (
    <div
      className={`border p-5 ${highlight ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-zinc-200 bg-white"}`}
      data-testid={testid}
    >
      <div className={`label-caps mb-2 flex items-center gap-2 ${highlight ? "opacity-80" : "text-zinc-500"}`}>
        <Icon size={12} strokeWidth={1.5} /> {label}
      </div>
      <div className="font-display text-2xl tracking-tight">{value}</div>
      {subtext && (
        <div className={`text-xs mt-1 font-mono ${highlight ? "opacity-70" : "text-zinc-500"}`}>{subtext}</div>
      )}
    </div>
  );
}

function CompactStat({ label, value, accent }) {
  return (
    <div className="border border-zinc-100 p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent ? "text-green-700" : ""}`}>{value}</div>
    </div>
  );
}

function ExternalLinkCard({ href, icon: Icon, label, sub, accent, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`border p-3 flex items-start gap-3 group ${accent ? "border-[#002FA7] hover:bg-[#002FA7] hover:text-white" : "border-zinc-200 hover:border-zinc-400"}`}
      data-testid={testid}
    >
      <Icon size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className={`text-xs font-mono truncate ${accent ? "opacity-80" : "text-zinc-500"}`}>{sub}</div>
      </div>
      <ExternalLink size={12} strokeWidth={1.5} className="flex-shrink-0 opacity-50" />
    </a>
  );
}

